// libraries
import { useRef, useState } from 'react';
import { notification, Tooltip, FormInstance, Switch, InputNumber } from 'antd';
import { Button, Form, Input, Select, DatePicker } from 'antd';
import { useSetRecoilState } from 'recoil';
import styled from 'styled-components';

import {
	faAngleDoubleLeft,
	faAngleDoubleRight,
	faMagnifyingGlass,
} from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import * as PostService from '../../../../services/bb-api/post';
import { txt } from '../../../../locales/i18n';
import { useFormFiller } from '../../../../hooks/form-filer';
import { SearchForm } from '../../../shared/search-form';
import { queryMatchState } from '../../../../recoil/atoms';

const initialValues: Record<string, any> = {
	amount: 200,
	status: 'not approved',
	postId: '',
	compact: true,
	date: [null, null],
	country: 'all',
	filter1: {
		target: 'all',
		search: '',
	},
	filter2: {
		target: 'all',
		search: '',
	},
};

export function PostListHeader({ setDataSource, useLoading }: any): JSX.Element {
	const [loading, setLoading] = useLoading;
	const setQueryMatch = useSetRecoilState(queryMatchState);
	const [advanced, setAdvanced] = useState(false);

	const formRef = useRef<FormInstance>(null);
	const setQueryParams = useFormFiller(formRef);

	async function handleSubmit(values: any): Promise<void> {
		setLoading(true);
		setQueryParams(values);

		try {
			const { posts, totalMatches } = await PostService.query(values);
			setDataSource(posts);
			setQueryMatch(totalMatches);
		} catch (error) {
			setDataSource([]);
			setQueryMatch(-1);

			const message: string = (error as Error).name;
			const description: string = (error as Error).message;
			notification.error({ message, description });
		}

		setLoading(false);
	}

	return (
		<SearchFormStyled ref={formRef} initialValues={initialValues} onFinish={handleSubmit}>
			<Tooltip title={advanced ? txt('global.simple') : txt('global.advanced')}>
				<Button type="link" onClick={() => setAdvanced((advanced) => !advanced)}>
					<FontAwesomeIcon icon={advanced ? faAngleDoubleLeft : faAngleDoubleRight} />
				</Button>
			</Tooltip>

			<Tooltip title={txt('global.post id')}>
				<Form.Item name="postId">
					<Input type="number" placeholder={txt('global.post id')} />
				</Form.Item>
			</Tooltip>

			<Tooltip title="Status">
				<Form.Item name="status">
					<Select dropdownMatchSelectWidth={false}>
						<Select.Option value="all">{txt('global.all')}</Select.Option>
						<Select.Option value="not approved">
							{txt('global.not approved')}
						</Select.Option>
						<Select.Option value="active">{txt('global.active')}</Select.Option>
						<Select.Option value="hidden">{txt('global.hidden')}</Select.Option>
						<Select.Option value="good">{txt('global.good')}</Select.Option>
					</Select>
				</Form.Item>
			</Tooltip>

			<Tooltip title={txt('global.filter')}>
				<Form.Item>
					<Input.Group compact>
						<Form.Item name={['filter1', 'target']} noStyle>
							<Select dropdownMatchSelectWidth={false}>
								<Select.Option value="all">{txt('global.all')}</Select.Option>
								<Select.Option value="title">{txt('global.title')}</Select.Option>
								<Select.Option value="email">Email</Select.Option>
								<Select.Option value="municipality">
									{txt('global.municipality')}
								</Select.Option>
								<Select.Option value="universe">
									{txt('global.universe')}
								</Select.Option>
								<Select.Option value="text">{txt('global.text')}</Select.Option>
								<Select.Option value="description">
									{txt('global.description')}
								</Select.Option>
							</Select>
						</Form.Item>

						<Form.Item name={['filter1', 'search']} noStyle>
							<Input
								style={{ width: 'fit-content' }}
								placeholder={txt('global.search')}
								allowClear
							/>
						</Form.Item>
					</Input.Group>
				</Form.Item>
			</Tooltip>

			<Tooltip title={txt('global.filter')}>
				<Form.Item>
					<Input.Group compact>
						<Form.Item name={['filter2', 'target']} noStyle>
							<Select dropdownMatchSelectWidth={false}>
								<Select.Option value="all">{txt('global.all')}</Select.Option>
								<Select.Option value="title">{txt('global.title')}</Select.Option>
								<Select.Option value="email">Email</Select.Option>
								<Select.Option value="municipality">
									{txt('global.municipality')}
								</Select.Option>
								<Select.Option value="universe">
									{txt('global.universe')}
								</Select.Option>
								<Select.Option value="text">{txt('global.text')}</Select.Option>
								<Select.Option value="description">
									{txt('global.description')}
								</Select.Option>
							</Select>
						</Form.Item>

						<Form.Item name={['filter2', 'search']} noStyle>
							<Input
								style={{ width: 'fit-content' }}
								placeholder={txt('global.search')}
								allowClear
							/>
						</Form.Item>
					</Input.Group>
				</Form.Item>
			</Tooltip>

			<Tooltip title={txt('global.date')}>
				<Form.Item name="date">
					<DatePicker.RangePicker
						allowEmpty={[true, true]}
						placeholder={[txt('global.from'), txt('global.to')]}
					/>
				</Form.Item>
			</Tooltip>

			{advanced && (
				<Tooltip title={txt('global.age')}>
					<div>
						<Form.Item noStyle name="ageFrom">
							<InputNumber placeholder={txt('global.from')} />
						</Form.Item>

						<Form.Item noStyle name="ageUntil">
							<InputNumber placeholder={txt('global.to')} />
						</Form.Item>
					</div>
				</Tooltip>
			)}

			<Tooltip title={txt('global.country')}>
				<Form.Item name="country">
					<Select dropdownMatchSelectWidth={false}>
						<Select.Option value="all">{txt('countries.all')}</Select.Option>
						<Select.Option value="1">{txt('countries.denmark')}</Select.Option>
						<Select.Option value="2">{txt('countries.sweden')}</Select.Option>
						<Select.Option value="3">{txt('countries.germany')}</Select.Option>
					</Select>
				</Form.Item>
			</Tooltip>

			<Tooltip title={txt('global.amount')}>
				<Form.Item name="amount">
					<Select dropdownMatchSelectWidth={false}>
						<Select.Option value={50}>50</Select.Option>
						<Select.Option value={200}>200</Select.Option>
						<Select.Option value={500}>500</Select.Option>
						<Select.Option value={1000}>1000</Select.Option>
						<Select.Option value={10000}>10000</Select.Option>
					</Select>
				</Form.Item>
			</Tooltip>

			<Tooltip title={txt('global.fast')}>
				<Form.Item name="compact" valuePropName="checked">
					<Switch />
				</Form.Item>
			</Tooltip>

			<Form.Item>
				<Button type="primary" htmlType="submit">
					<FontAwesomeIcon icon={faMagnifyingGlass} />
				</Button>
			</Form.Item>
		</SearchFormStyled>
	);
}

const SearchFormStyled = styled(SearchForm)`
	#postId {
		width: 10ch;
	}

	#filter1_search,
	#filter2_search {
		width: 12ch;
	}

	.ant-picker-input input {
		width: 10ch;
	}

	.ant-input-number {
		width: 6ch;
	}
`;
